import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  type ReactNode,
} from 'react';
import type { InventoryItem, SlotLocation } from '../types';

export interface DragState {
  isDragging: boolean;
  item: InventoryItem | null;
  from: SlotLocation | null;
  x: number;
  y: number;
}

interface DragDropContextValue {
  dragState: DragState;
  rejectedLocation: SlotLocation | null;
  startDrag: (item: InventoryItem, from: SlotLocation, e: React.PointerEvent) => void;
}

const DragDropContext = createContext<DragDropContextValue | null>(null);

const DRAG_THRESHOLD = 6;
const REJECT_FLASH_MS = 600;

interface Props {
  children: ReactNode;
  onDrop: (from: SlotLocation, to: SlotLocation) => void;
  canDrop: (item: InventoryItem, from: SlotLocation, to: SlotLocation) => boolean;
}

export function DragDropProvider({ children, onDrop, canDrop }: Props) {
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    item: null,
    from: null,
    x: 0,
    y: 0,
  });
  const [rejectedLocation, setRejectedLocation] = useState<SlotLocation | null>(null);

  const startPos = useRef<{ x: number; y: number } | null>(null);
  const pendingDrag = useRef<{ item: InventoryItem; from: SlotLocation } | null>(null);
  const isDraggingRef = useRef(false);
  const onDropRef = useRef(onDrop);
  const canDropRef = useRef(canDrop);
  const rejectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  onDropRef.current = onDrop;
  canDropRef.current = canDrop;

  const endDrag = useCallback(() => {
    isDraggingRef.current = false;
    startPos.current = null;
    pendingDrag.current = null;
    setDragState({ isDragging: false, item: null, from: null, x: 0, y: 0 });
  }, []);

  const startDrag = useCallback((item: InventoryItem, from: SlotLocation, e: React.PointerEvent) => {
    e.preventDefault();
    startPos.current = { x: e.clientX, y: e.clientY };
    pendingDrag.current = { item, from };
  }, []);

  useEffect(() => {
    const handleMove = (e: PointerEvent) => {
      if (!pendingDrag.current) return;

      if (!isDraggingRef.current && startPos.current) {
        const dx = e.clientX - startPos.current.x;
        const dy = e.clientY - startPos.current.y;
        if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return;
        isDraggingRef.current = true;
      }

      if (isDraggingRef.current) {
        setDragState({
          isDragging: true,
          item: pendingDrag.current!.item,
          from: pendingDrag.current!.from,
          x: e.clientX,
          y: e.clientY,
        });
      }
    };

    const handleUp = (e: PointerEvent) => {
      if (isDraggingRef.current && pendingDrag.current) {
        const elements = document.elementsFromPoint(e.clientX, e.clientY);
        for (const el of elements) {
          const attr = (el as HTMLElement).dataset?.slotLocation;
          if (attr) {
            try {
              const to: SlotLocation = JSON.parse(attr);
              const { item, from } = pendingDrag.current;
              if (canDropRef.current(item, from, to)) {
                onDropRef.current(from, to);
              } else {
                if (rejectTimer.current) clearTimeout(rejectTimer.current);
                setRejectedLocation(to);
                rejectTimer.current = setTimeout(() => setRejectedLocation(null), REJECT_FLASH_MS);
              }
            } catch {
              // ignore parse errors
            }
            break;
          }
        }
      }
      endDrag();
    };

    const handleCancel = () => endDrag();

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleCancel);

    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleCancel);
      if (rejectTimer.current) clearTimeout(rejectTimer.current);
    };
  }, [endDrag]);

  const contextValue = useMemo(
    () => ({ dragState, rejectedLocation, startDrag }),
    [dragState, rejectedLocation, startDrag],
  );

  return (
    <DragDropContext.Provider value={contextValue}>
      {children}
    </DragDropContext.Provider>
  );
}

export function useDragDropContext() {
  const ctx = useContext(DragDropContext);
  if (!ctx) throw new Error('useDragDropContext must be used inside DragDropProvider');
  return ctx;
}
